import React from 'react';
import ShippingSelector from './ShippingSelector';
import OrderCustomerPanel from './OrderCustomerPanel';
import OrderSettingsPanel from './OrderSettingsPanel';
import OrderProductsPanel from './OrderProductsPanel';
import OrderCustomerSelector from './OrderCustomerSelector';
import { useOrderForm } from '../../hooks/useOrderForm';
import './OrderForm.css';

const OrderForm = ({ editingOrder = null, onSave, onCancel }) => {
  const {
    savedOrderId,
    formData, setFormData,
    customers,
    selectedCustomer, setSelectedCustomer,
    showCustomerSelector, setShowCustomerSelector,
    productSearch, products,
    loading, errors,
    selectedLocation, availableLocations,
    handleProductSearchChange,
    handleCustomerSelect,
    handleAddCustomer,
    handleAddProduct,
    handleRemoveProduct,
    handleProductChange,
    calculateTotals,
    handleSubmit,
  } = useOrderForm(editingOrder, onSave);

  const { wartoscNetto, wartoscBrutto } = calculateTotals();

  return (
    <div
      className="order-modal-overlay"
      style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, width: '100vw', height: '100vh'
      }}
    >
      <div style={{
        backgroundColor: 'var(--card-bg)', borderRadius: '8px',
        maxWidth: '95vw', width: '1100px', maxHeight: '90vh',
        overflow: 'hidden', boxShadow: '0 4px 20px rgba(0,0,0,0.3)'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, var(--success) 0%, var(--success) 100%)',
          color: '#fff' , padding: '0.5rem 0.75rem',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center'
        }}>
          <div>
            <h3 style={{ margin: 0, fontSize: '12px', fontWeight: '600' }}>
              {editingOrder ? 'Edytuj zamowienie' : 'Nowe zamowienie'}
            </h3>
            <div style={{ fontSize: '10px', opacity: 0.9, marginTop: '2px' }}>
              {new Date().toLocaleDateString('pl-PL')} | Lokalizacja: {selectedLocation?.nazwa || 'Domyslna'}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            style={{ padding: '4px 10px', backgroundColor: 'var(--bg-secondary)', color: 'var(--text-secondary)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '11px' }}
          >
            X Zamknij
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '0.5rem', overflow: 'auto', maxHeight: 'calc(90vh - 120px)' }}>
            {errors.general && (
              <div style={{ backgroundColor: 'var(--danger-light)', color: 'var(--danger)', padding: '0.4rem 0.5rem', borderRadius: '4px', marginBottom: '0.5rem', border: '1px solid var(--danger-light)', fontSize: '10px' }}>
                {errors.general}
              </div>
            )}

            {/* Top section: Customer + Settings */}
            <div style={{ display: 'grid', gap: '0.5rem', gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: '0.5rem' }}>
              <OrderCustomerPanel
                selectedCustomer={selectedCustomer}
                setSelectedCustomer={setSelectedCustomer}
                setFormData={setFormData}
                setShowCustomerSelector={setShowCustomerSelector}
                errors={errors}
              />
              <OrderSettingsPanel
                formData={formData}
                setFormData={setFormData}
                availableLocations={availableLocations}
              />
            </div>

            {/* Shipping section */}
            {formData.typ_realizacji === 'dostawa' && (
              <div style={{ marginTop: '0.5rem' }}>
                <ShippingSelector
                  selectedMethod={formData.shipping_method}
                  selectedPoint={formData.shipping_point}
                  onMethodChange={(method) => setFormData(prev => ({
                    ...prev,
                    shipping_method: method,
                    shipping_point: method !== 'inpost_locker_standard' ? null : prev.shipping_point
                  }))}
                  onPointChange={(point) => setFormData(prev => ({ ...prev, shipping_point: point }))}
                  receiverAddress={selectedCustomer ? {
                    street: selectedCustomer.wysylka_ulica || selectedCustomer.adres || '',
                    city: selectedCustomer.wysylka_miasto || selectedCustomer.miasto || '',
                    post_code: selectedCustomer.wysylka_kod_pocztowy || selectedCustomer.kod_pocztowy || ''
                  } : null}
                  orderId={editingOrder?.id || savedOrderId}
                  customerPhone={selectedCustomer?.telefon || selectedCustomer?.phone || ''}
                  customerEmail={selectedCustomer?.email || ''}
                  onCreateLabel={(labelData) => { onSave(); }}
                />
              </div>
            )}

            {/* Products section */}
            <OrderProductsPanel
              formData={formData}
              productSearch={productSearch}
              products={products}
              errors={errors}
              onProductSearchChange={handleProductSearchChange}
              onAddProduct={handleAddProduct}
              onRemoveProduct={handleRemoveProduct}
              onProductChange={handleProductChange}
              wartoscNetto={wartoscNetto}
              wartoscBrutto={wartoscBrutto}
            />
          </div>

          {/* Footer */}
          <div style={{
            padding: '0.5rem 0.75rem', backgroundColor: 'var(--bg)',
            borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: '6px'
          }}>
            <button
              type="button"
              onClick={onCancel}
              style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '500', border: '1px solid var(--border)', cursor: 'pointer', backgroundColor: 'var(--card-bg)', color: 'var(--text-secondary)' }}
            >
              Anuluj
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', border: '1px solid var(--success)', color: 'var(--success)' , opacity: loading ? 0.7 : 1 }}
            >
              {loading ? 'Zapisywanie...' : (editingOrder || savedOrderId ? 'Zapisz zmiany' : 'Utworz zamowienie')}
            </button>

            {savedOrderId && (
              <button
                type="button"
                onClick={onSave}
                style={{ padding: '6px 12px', fontSize: '12px', fontWeight: '600', border: '1px solid var(--text-muted)', cursor: 'pointer', backgroundColor: 'var(--bg)', color: 'var(--text-secondary)' }}
              >
                Zamknij bez etykiety
              </button>
            )}
          </div>
        </form>
      </div>

      {showCustomerSelector && (
        <OrderCustomerSelector
          customers={customers}
          onSelect={handleCustomerSelect}
          onAddCustomer={handleAddCustomer}
          onClose={() => setShowCustomerSelector(false)}
        />
      )}
    </div>
  );
};

export default OrderForm;
